import type { JSX } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import log from 'loglevel';

import { identifySentryUser } from '../../sentry';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { EndUserLoading, isEndUserPage } from '../EndUserLoading/EndUserLoading';

// =============================================================================
// Types
// =============================================================================

export interface LocalAuthUser {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  picture?: string;
  locale?: string;
  roles?: string[];
  tenantId?: string;
}

interface LocalAuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: LocalAuthUser | null;
  error: Error | null;
  accessToken: string | null;
  getAccessTokenSilently: () => Promise<string>;
  login: (email: string, password: string) => Promise<void>;
  logout: (options?: { returnTo?: string }) => Promise<void>;
  updateToken: (minValidity?: number) => Promise<boolean>;
  /** Force-fetch a fresh token and re-derive the user (e.g. after a profile edit). */
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasRealmRole: (role: string) => boolean;
}

// =============================================================================
// Configuration
// =============================================================================

// `??` (not `||`) so an intentionally-empty VITE_API_ENDPOINT (staging/preview,
// where the worker proxies the API same-origin) is preserved as "" → relative
// URLs, instead of falling back to localhost.
const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';
const TOKEN_REFRESH_INTERVAL = 60_000;
// Refresh the token when it has less than 2 minutes left. This gives enough runway
// for background-tab timer throttling (browsers can delay timers by 60s+).
const MIN_TOKEN_VALIDITY = 120;

// =============================================================================
// Context
// =============================================================================

const LocalAuthContext = createContext<LocalAuthContextValue | null>(null);

// =============================================================================
// Helpers
// =============================================================================

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = atob(base64);
  return JSON.parse(jsonPayload);
}

function extractUserFromPayload(payload: Record<string, unknown>): LocalAuthUser {
  const realmAccess = payload.realm_access as { roles?: string[] } | undefined;
  return {
    sub: (payload.sub as string) || '',
    email: payload.email as string | undefined,
    email_verified: payload.email_verified as boolean | undefined,
    name: payload.name as string | undefined,
    given_name: payload.given_name as string | undefined,
    family_name: payload.family_name as string | undefined,
    preferred_username: payload.preferred_username as string | undefined,
    picture: payload.picture as string | undefined,
    locale: payload.locale as string | undefined,
    roles: (payload.roles as string[]) || realmAccess?.roles || [],
    tenantId: (payload.tenantId as string) || (payload.tenant_id as string) || undefined,
  };
}

function getTokenExpiry(token: string): number {
  const payload = decodeJwtPayload(token);
  return (payload.exp as number) || 0;
}

type ErrorWithStatus = Error & { status?: number };

/** True when the session/token is invalid and re-auth is required (not transient/network). */
function isDefinitiveAuthFailure(err: unknown): boolean {
  if (err == null) return false;
  if (typeof err === 'object' && 'status' in err) {
    const status = (err as { status: unknown }).status;
    if (typeof status === 'number') {
      if (status === 401 || status === 403) return true;
      return false;
    }
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (
      msg.includes('invalid_grant') ||
      msg.includes('login_required') ||
      msg.includes('consent_required')
    ) {
      return true;
    }
  }
  return false;
}

// =============================================================================
// Custom Hook
// =============================================================================

export function useLocalAuth(): LocalAuthContextValue {
  const context = useContext(LocalAuthContext);

  if (!context) {
    throw new Error('useLocalAuth must be used within a LocalAuthProvider');
  }

  return context;
}

// =============================================================================
// Provider Component
// =============================================================================

interface LocalAuthProviderProps {
  children: React.ReactNode;
}

export default function LocalAuthProvider({ children }: LocalAuthProviderProps): JSX.Element {
  const accessTokenRef = useRef<string | null>(null);
  // Deduplicates concurrent refresh calls so we never fire multiple /api/auth/token
  // requests at the same time (e.g. several Apollo queries sent in parallel).
  const refreshPromiseRef = useRef<Promise<string> | null>(null);
  const [user, setUser] = useState<LocalAuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initStarted = useRef(false);

  // ==========================================================================
  // Initial refresh on mount
  // ==========================================================================

  useEffect(() => {
    if (initStarted.current) {
      log.info('LocalAuth initialization already in progress, skipping...');
      return;
    }
    initStarted.current = true;

    log.info('Initializing LocalAuth...');

    fetch(`${API_BASE_URL}/api/auth/token`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    })
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          const accessToken = data.token;
          accessTokenRef.current = accessToken;
          const payload = decodeJwtPayload(accessToken);
          const extractedUser = extractUserFromPayload(payload);
          setUser(extractedUser);
          setIsAuthenticated(true);
          log.info('LocalAuth initialized. Authenticated: true');
        } else {
          setIsAuthenticated(false);
          log.info('LocalAuth initialized. Authenticated: false');
        }
      })
      .catch((err) => {
        log.error('LocalAuth initialization failed:', err);
        setIsAuthenticated(false);
        setError(new Error('Authentication initialization failed'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // ==========================================================================
  // Methods
  // ==========================================================================

  // Single shared helper that fetches a fresh token from the session cookie.
  // Deduplicates concurrent calls so only one HTTP request is in-flight at a time.
  const fetchFreshToken = useCallback((): Promise<string> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const promise = fetch(`${API_BASE_URL}/api/auth/token`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) {
          const err = new Error('Session expired') as ErrorWithStatus;
          err.status = response.status;
          throw err;
        }
        const data = await response.json();
        accessTokenRef.current = data.token;
        const payload = decodeJwtPayload(data.token);
        setUser(extractUserFromPayload(payload));
        return data.token as string;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = promise;
    return promise;
  }, []);

  const getAccessTokenSilently = useCallback(async (): Promise<string> => {
    const currentToken = accessTokenRef.current;
    const now = Math.floor(Date.now() / 1000);

    // Return the current token if it still has enough runway.
    if (currentToken) {
      const exp = getTokenExpiry(currentToken);
      if (exp - now > MIN_TOKEN_VALIDITY) {
        return currentToken;
      }
    }

    // Token is missing, expired, or about to expire — refresh via session cookie.
    return fetchFreshToken();
  }, [fetchFreshToken]);

  // ==========================================================================
  // Proactive token refresh
  // ==========================================================================

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const intervalId = setInterval(async () => {
      const currentToken = accessTokenRef.current;
      const now = Math.floor(Date.now() / 1000);

      // Only act when the token is nearing expiry (or already gone).
      const exp = currentToken ? getTokenExpiry(currentToken) : 0;
      if (exp - now > MIN_TOKEN_VALIDITY) return;

      try {
        await fetchFreshToken();
        log.debug('LocalAuth token refreshed proactively');
      } catch (err) {
        log.error('LocalAuth proactive token refresh failed:', err);
        accessTokenRef.current = null;
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      }
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, fetchFreshToken]);

  // Background tabs throttle timers heavily, so the interval above may not run before
  // the JWT expires. When the user returns, refresh immediately if the token is stale.
  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const clearSessionAndRedirectToLogin = (reason: unknown) => {
        log.error('LocalAuth token refresh after tab became visible failed (session invalid):', reason);
        accessTokenRef.current = null;
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      };

      try {
        await getAccessTokenSilently();
      } catch (err) {
        if (isDefinitiveAuthFailure(err)) {
          clearSessionAndRedirectToLogin(err);
          return;
        }
        log.warn('LocalAuth token refresh after tab became visible failed (transient):', err);
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          await getAccessTokenSilently();
        } catch (retryErr) {
          if (isDefinitiveAuthFailure(retryErr)) {
            clearSessionAndRedirectToLogin(retryErr);
            return;
          }
          log.error('LocalAuth token refresh after visibility retry failed:', retryErr);
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isAuthenticated, getAccessTokenSilently]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    // Mobile keyboards / autofill routinely append whitespace to the email,
    // which the backend rejects as invalid credentials. Normalize before send.
    const normalizedEmail = email.trim();

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/api/auth/sign-in/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });
    } catch (err) {
      // fetch() itself rejecting means the request never got an HTTP response
      // (offline, DNS, TLS, blocked). Surface that as its own step so a login
      // report can distinguish it from a rejected credential or token failure.
      const detail = err instanceof Error ? err.message : String(err);
      log.error('Sign-in request failed before reaching the server:', err);
      throw new Error(`Could not reach the sign-in service (network error: ${detail}).`);
    }

    if (!response.ok) {
      let message = response.status === 401
        ? 'Invalid email or password.'
        : 'Sign in failed. Please try again.';
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
        else if (body?.error?.message) message = body.error.message;
        else if (body?.error && typeof body.error === 'string') message = body.error;
      } catch {
        // response wasn't JSON – keep default message
      }
      log.error('Sign-in failed:', { status: response.status, message });
      throw new Error(message);
    }

    // Sign-in sets session cookies; now fetch a JWT access token
    let tokenResponse: Response;
    try {
      tokenResponse = await fetch(`${API_BASE_URL}/api/auth/token`, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      log.error('Token request failed before reaching the server:', err);
      throw new Error(`Signed in, but could not reach the token service (network error: ${detail}).`);
    }

    if (!tokenResponse.ok) {
      // Include the status: a 401 here means the session cookie set by sign-in
      // was not sent back (cookie blocked/dropped), while a 403/5xx points at
      // the server or a proxy in between.
      log.error('Token fetch after sign-in failed:', { status: tokenResponse.status });
      throw new Error(`Failed to obtain access token (HTTP ${tokenResponse.status}).`);
    }

    const tokenData = await tokenResponse.json();
    const accessToken = tokenData.token;
    accessTokenRef.current = accessToken;
    const payload = decodeJwtPayload(accessToken);
    setUser(extractUserFromPayload(payload));
    setIsAuthenticated(true);
    setError(null);
  }, []);

  const logout = useCallback(async (options?: { returnTo?: string }): Promise<void> => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      log.error('LocalAuth logout error:', err);
    } finally {
      accessTokenRef.current = null;
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/login';
    }
  }, []);

  const updateToken = useCallback(async (minValidity?: number): Promise<boolean> => {
    const currentToken = accessTokenRef.current;
    if (!currentToken) return false;

    const exp = getTokenExpiry(currentToken);
    const now = Math.floor(Date.now() / 1000);
    const validity = minValidity ?? 30;

    if (exp - now <= validity) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/token`, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });

        if (response.ok) {
          const data = await response.json();
          accessTokenRef.current = data.token;
          const payload = decodeJwtPayload(data.token);
          setUser(extractUserFromPayload(payload));
          return true;
        }
      } catch (err) {
        log.error('LocalAuth updateToken error:', err);
      }
      return false;
    }

    return false;
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    await fetchFreshToken();
  }, [fetchFreshToken]);

  const hasRole = useCallback(
    (role: string): boolean => {
      return user?.roles?.includes(role) ?? false;
    },
    [user],
  );

  const hasRealmRole = useCallback(
    (role: string): boolean => {
      return user?.roles?.includes(role) ?? false;
    },
    [user],
  );

  // ==========================================================================
  // Sentry identity
  // ==========================================================================

  // Every path that changes who is signed in funnels through this `user` state
  // (initial refresh, login, token refresh, logout), so one effect keeps Sentry
  // — errors and session replays alike — in step with it.
  useEffect(() => {
    identifySentryUser(user);
  }, [user]);

  // ==========================================================================
  // Context Value
  // ==========================================================================

  const contextValue = useMemo<LocalAuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      error,
      accessToken: accessTokenRef.current,
      getAccessTokenSilently,
      login,
      logout,
      updateToken,
      refreshUser,
      hasRole,
      hasRealmRole,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      error,
      getAccessTokenSilently,
      login,
      logout,
      updateToken,
      refreshUser,
      hasRole,
      hasRealmRole,
    ],
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  if (isLoading) {
    return isEndUserPage() ? <EndUserLoading /> : <LoadingOverlay />;
  }

  return <LocalAuthContext.Provider value={contextValue}>{children}</LocalAuthContext.Provider>;
}

// =============================================================================
// Named Exports
// =============================================================================

export { LocalAuthProvider };
